import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';
import { ESCAPE_PRIORITY, useEscapeAction } from '@peruselab/structure-editor/components';

interface RepeatUnitPopoverProps {
  /** Client point to open at -- where the count label was pressed, or the new label. */
  readonly x: number;
  readonly y: number;
  readonly countMin: number;
  /** Undefined = open-ended. */
  readonly countMax: number | undefined;
  /** Applies a range; false when it was refused. */
  readonly onApply: (countMin: number, countMax: number | undefined) => boolean;
  readonly onRemove: () => void;
  readonly onClose: () => void;
}

// #656 (Markush M3e) -- the repeat unit's count editor, a small pop-up at its bracket (owner
// decision: try this on touch before falling back to a panel section). Same floating pattern as
// the canvas's PaperContextMenu -- a portal clamped to the visible area -- but closed only by an
// outside press, Escape or its close button: resize/scroll (a phone's keyboard) reposition it. A range applies on Enter or when a field loses focus, one undo step
// each; the open-ended box applies at once.
export function RepeatUnitPopover({ x, y, countMin, countMax, onApply, onRemove, onClose }: RepeatUnitPopoverProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const minRef = useRef<HTMLInputElement>(null);
  const [min, setMin] = useState(String(countMin));
  const [max, setMax] = useState(countMax === undefined ? '' : String(countMax));
  const [error, setError] = useState<string | null>(null);
  const openEnded = countMax === undefined;

  // Follow the unit when undo/redo changes it underneath the open pop-up.
  useEffect(() => {
    setMin(String(countMin));
    setMax(countMax === undefined ? '' : String(countMax));
  }, [countMin, countMax]);

  // Keep the pop-up inside the *visible* area. On a phone, focusing a field raises the on-screen
  // keyboard, which resizes/scrolls the visual viewport; the pop-up must move above the keyboard,
  // not close (owner's mobile test on #656: it vanished the moment a field was tapped).
  const place = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const view = window.visualViewport;
    const left = view?.offsetLeft ?? 0;
    const top = view?.offsetTop ?? 0;
    const width = view?.width ?? window.innerWidth;
    const height = view?.height ?? window.innerHeight;
    panel.style.left = `${Math.max(left + 8, Math.min(x + 8, left + width - rect.width - 8))}px`;
    panel.style.top = `${Math.max(top + 8, Math.min(y + 8, top + height - rect.height - 8))}px`;
  }, [x, y]);

  useLayoutEffect(() => {
    place();
    // Focus the first field for mouse/keyboard users; on touch that would pop the keyboard over
    // the drawing before the user asked to type.
    if (!window.matchMedia?.('(pointer: coarse)').matches) minRef.current?.focus({ preventScroll: true });
  }, [place]);

  // #762 -- Escape closes through the shared arbiter (overlay priority).
  useEscapeAction(ESCAPE_PRIORITY.overlay, () => {
    onClose();
    return true;
  });
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !panelRef.current?.contains(event.target)) onClose();
    };
    window.addEventListener('pointerdown', outside, true);
    // Resize and scroll (keyboard, rotation, page scroll) reposition; they never close.
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    return () => {
      window.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('scroll', place);
    };
  }, [onClose, place]);

  const apply = (nextMin: string, nextMax: string, nextOpenEnded: boolean) => {
    const parsedMin = Number(nextMin);
    const parsedMax = nextOpenEnded ? undefined : Number(nextMax);
    const valid =
      nextMin.trim() !== '' &&
      Number.isInteger(parsedMin) &&
      parsedMin >= 0 &&
      (parsedMax === undefined ||
        (nextMax.trim() !== '' && Number.isInteger(parsedMax) && parsedMax >= Math.max(1, parsedMin)));
    if (!valid) {
      setError(t('repeatUnit.invalid'));
      return;
    }
    setError(null);
    if (parsedMin === countMin && parsedMax === countMax) return;
    if (!onApply(parsedMin, parsedMax)) setError(t('repeatUnit.invalid'));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    apply(min, max, openEnded);
  };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('repeatUnit.title')}
      className="repeat-unit-popover"
      data-testid="repeat-unit-popover"
    >
      <div className="repeat-unit-popover-head">
        <strong>{t('repeatUnit.title')}</strong>
        <button
          type="button"
          className="icon-button"
          aria-label={t('repeatUnit.close')}
          title={t('repeatUnit.close')}
          onClick={onClose}
        >
          <Icon name="close" size="1rem" />
        </button>
      </div>
      <form className="repeat-unit-popover-fields" onSubmit={submit}>
        <label>
          <span>{t('repeatUnit.min')}</span>
          <input
            ref={minRef}
            type="number"
            inputMode="numeric"
            min={0}
            data-testid="repeat-unit-min"
            value={min}
            onChange={(event) => setMin(event.target.value)}
            onBlur={() => apply(min, max, openEnded)}
          />
        </label>
        <label>
          <span>{t('repeatUnit.max')}</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            data-testid="repeat-unit-max"
            value={max}
            disabled={openEnded}
            onChange={(event) => setMax(event.target.value)}
            onBlur={() => apply(min, max, openEnded)}
          />
        </label>
        <label className="repeat-unit-popover-open">
          <input
            type="checkbox"
            data-testid="repeat-unit-open-ended"
            checked={openEnded}
            onChange={(event) => {
              const nextOpen = event.target.checked;
              // Re-closing an open-ended unit starts from max = min (at least 1).
              const nextMax = nextOpen ? max : String(Math.max(1, Number(min) || 1));
              setMax(nextOpen ? '' : nextMax);
              apply(min, nextMax, nextOpen);
            }}
          />
          <span>{t('repeatUnit.openEnded')}</span>
        </label>
        {/* Enter in a field submits; the button is for touch, where there is no Enter key to hand. */}
        <button
          type="submit"
          data-testid="repeat-unit-apply"
          className="icon-button"
          aria-label={t('repeatUnit.apply')}
          title={t('repeatUnit.apply')}
        >
          <Icon name="check" />
        </button>
      </form>
      {error ? (
        <p className="repeat-unit-popover-error" role="alert">
          {error}
        </p>
      ) : null}
      <button type="button" className="repeat-unit-popover-remove" data-testid="repeat-unit-remove" onClick={onRemove}>
        <Icon name="delete" size="1rem" />
        <span>{t('repeatUnit.remove')}</span>
      </button>
    </div>,
    document.body
  );
}
